import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Calendar, Clock, Tag, ChevronRight, ArrowLeft } from 'lucide-react';
import blogArticles, { getArticle } from '../data/blogArticles';
import JsonLd from '../components/JsonLd';
import SizeCalculator from '../components/SizeCalculator';
import { SITE_URL, SITE_NAME } from '../data/seoConfig';

const Table = ({ table }) => (
  <div className="overflow-x-auto my-6 rounded-xl border border-gray-200">
    <table className="w-full text-sm text-left">
      <thead className="bg-orange-50 text-orange-800">
        <tr>
          {table.headers.map((h) => (
            <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {table.rows.map((row, i) => (
          <tr key={i} className="bg-white">
            {row.map((cell, j) => (
              <td key={j} className={'px-4 py-3 ' + (j === 0 ? 'font-medium text-gray-900' : 'text-gray-600')}>
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const BlogArticlePage = () => {
  const { slug } = useParams();
  const article = getArticle(slug);

  if (!article) return <Navigate to="/blog" replace />;

  const url = SITE_URL + '/blog/' + article.slug;
  const related = blogArticles.filter((a) => a.slug !== article.slug);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    description: article.metaDescription,
    image: article.image,
    datePublished: article.dateISO,
    dateModified: article.dateISO,
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    author: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
    publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
    articleSection: article.category,
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faq.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: SITE_URL + '/' },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: SITE_URL + '/blog' },
      { '@type': 'ListItem', position: 3, name: article.title, item: url },
    ],
  };

  return (
    <article className="min-h-screen bg-white pt-24" data-testid="blog-article-page">
      <JsonLd data={articleJsonLd} />
      <JsonLd data={faqJsonLd} />
      <JsonLd data={breadcrumbJsonLd} />

      <header className="bg-gradient-to-r from-orange-600 to-orange-700 py-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm text-orange-100 mb-5" aria-label="Fil d'ariane">
            <Link to="/" className="hover:text-white">Accueil</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/blog" className="hover:text-white" data-testid="breadcrumb-blog">Blog</Link>
          </nav>
          <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-white/90 mb-3">
            <Tag className="w-3 h-3" /> {article.category}
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-5" data-testid="article-h1">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-5 text-sm text-orange-100">
            <span className="flex items-center gap-2"><Calendar className="w-4 h-4" />{article.date}</span>
            <span className="flex items-center gap-2"><Clock className="w-4 h-4" />{article.readingTime} de lecture</span>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <img
          src={article.image}
          alt={article.imageAlt}
          className="w-full aspect-[16/9] object-cover rounded-2xl mb-10"
          loading="lazy"
        />

        <p className="text-lg text-gray-800 leading-relaxed mb-10 border-l-4 border-orange-500 pl-5">
          {article.intro}
        </p>

        {/* Sommaire */}
        <nav className="bg-gray-50 rounded-2xl p-6 mb-12" data-testid="article-toc">
          <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-3">Sommaire</h2>
          <ol className="space-y-2 list-decimal list-inside">
            {article.sections.map((s, i) => (
              <li key={i}>
                <a href={'#section-' + i} className="text-sm text-orange-600 hover:underline">{s.heading}</a>
              </li>
            ))}
          </ol>
        </nav>

        {article.sections.map((section, i) => (
          <React.Fragment key={i}>
          <section id={'section-' + i} className="mb-12 scroll-mt-28">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5">{section.heading}</h2>
            {(section.paragraphs || []).map((p, j) => (
              <p key={j} className="text-base text-gray-600 leading-relaxed mb-4">{p}</p>
            ))}
            {section.list && (
              <ul className="space-y-2 my-5">
                {section.list.map((item, j) => (
                  <li key={j} className="flex gap-3 text-base text-gray-600">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-orange-500 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
            {section.table && <Table table={section.table} />}
          </section>
          {article.calculatorAfter === i && <SizeCalculator />}
          </React.Fragment>
        ))}

        {/* FAQ */}
        <section className="mb-12" data-testid="article-faq">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Questions fréquentes</h2>
          <div className="space-y-4">
            {article.faq.map((f, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-5">
                <h3 className="font-semibold text-gray-900 mb-2">{f.question}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{f.answer}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-r from-orange-600 to-orange-700 rounded-2xl p-8 text-center mb-14">
          <h2 className="text-2xl font-bold text-white mb-3">Un projet d'événement en Normandie ?</h2>
          <p className="text-orange-100 mb-6">
            Décrivez-nous votre événement : nous vous conseillons la structure adaptée et vous envoyons un devis gratuit.
          </p>
          <Link
            to="/devis"
            className="inline-flex items-center gap-2 px-8 py-3 bg-white text-orange-600 font-semibold rounded-full hover:bg-orange-50 transition-colors"
            data-testid="article-cta-devis"
          >
            Demander mon devis gratuit <ChevronRight className="w-4 h-4" />
          </Link>
        </section>

        {/* Articles liés */}
        <section>
          <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-5">À lire aussi</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {related.map((a) => (
              <Link
                key={a.slug}
                to={'/blog/' + a.slug}
                className="group block bg-gray-50 rounded-xl p-5 hover:bg-orange-50 transition-colors"
                data-testid={'related-article-' + a.slug}
              >
                <span className="text-xs text-orange-500 font-medium">{a.category}</span>
                <h3 className="text-base font-semibold text-gray-900 mt-1 group-hover:text-orange-600 transition-colors">
                  {a.title}
                </h3>
              </Link>
            ))}
          </div>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 mt-8 text-sm font-medium text-orange-600 hover:underline"
            data-testid="back-to-blog"
          >
            <ArrowLeft className="w-4 h-4" /> Tous les articles
          </Link>
        </section>
      </div>
    </article>
  );
};

export default BlogArticlePage;
